<script>
  export let question;
  export let updateWorkingQuestion;
  export let propertyLabel = "Property";
  export let keyPlaceholder = "property-name";
  export let valuePlaceholder = "__answers__";

  let propsData;
  
  function getPropsData() {
    const dataset = question.dataset || [];
    const propsHandler = dataset.find(handler => 
      handler[0] === 'selfProps' || handler[0] === 'nodeProps'
    );
    
    if (propsHandler && propsHandler[propsHandler.length - 1]) {
      const props = propsHandler[propsHandler.length - 1];
      return Object.entries(props).map(([key, value]) => ({ key, value: String(value) }));
    }
    
    return [{ key: keyPlaceholder, value: valuePlaceholder }];
  }
  
  function updatePropsData(newProps) {
    const dataset = [...(question.dataset || [])];
    const propsHandlerIndex = dataset.findIndex(handler => 
      handler[0] === 'selfProps' || handler[0] === 'nodeProps'
    );
    
    if (propsHandlerIndex !== -1) {
      const handler = [...dataset[propsHandlerIndex]];
      const propsObject = {};
      
      // Convert array back to object
      newProps.forEach(prop => {
        if (prop.key && prop.key.trim()) {
          propsObject[prop.key] = prop.value;
        }
      });
      
      handler[handler.length - 1] = propsObject;
      dataset[propsHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function addProperty() {
    const currentProps = getPropsData();
    const newProps = [...currentProps, { 
      key: keyPlaceholder, 
      value: question.manyToMany ? '__manyAnswers__' : '__answers__' 
    }];
    updatePropsData(newProps);
  }
  
  function removeProperty(index) {
    const currentProps = getPropsData();
    if (currentProps.length > 1) {
      const newProps = currentProps.filter((_, i) => i !== index);
      updatePropsData(newProps);
    }
  }
  
  function updateProperty(index, field, value) {
    const currentProps = getPropsData();
    const newProps = [...currentProps];
    newProps[index] = { ...newProps[index], [field]: value };
    updatePropsData(newProps);
  }
  
  // Reactive derived state - automatically updates when question changes
  $: question, propsData = getPropsData();
</script>

<div class="props-editor-section">
  <h5>{propertyLabel} Configuration</h5>
  
  <div class="props-grid">
    {#each propsData as prop, index}
      <div class="prop-row">
        <div class="prop-number">
          <span class="number-badge">{index + 1}</span>
        </div>
        
        <div class="prop-key-input">
          <label>Property Name</label>
          <input 
            type="text"
            value={prop.key}
            on:input={(e) => updateProperty(index, 'key', e.target.value)}
            placeholder={keyPlaceholder}
            class="key-field"
          />
        </div>
        
        <div class="prop-value-input">
          <label>Property Value</label>
          <select 
            value={prop.value}
            on:change={(e) => updateProperty(index, 'value', e.target.value)}
            class="value-field"
          >
            <option value="__answers__">__answers__ (All answers)</option>
            <option value="__manyAnswers__">__manyAnswers__ (Many-to-many)</option>
            {#if question.kind === 'Quant'}
              {#each Array.from({length: 10}, (_, i) => i) as answerIndex}
                <option value="__answer{answerIndex}__">__answer{answerIndex}__ (Answer {answerIndex + 1})</option>
              {/each}
            {/if}
            <option value="custom">Custom value...</option>
          </select>
          
          {#if prop.value === 'custom'}
            <input 
              type="text"
              value=""
              on:input={(e) => updateProperty(index, 'value', e.target.value)}
              placeholder="Enter custom value"
              class="custom-value-field"
            />
          {/if}
        </div>
        
        <div class="prop-actions">
          {#if propsData.length > 1}
            <button 
              class="btn btn-small btn-danger"
              on:click={() => removeProperty(index)}
              title="Remove property"
            >
              ×
            </button>
          {/if}
        </div>
      </div>
    {/each}
  </div>
  
  <div class="props-footer">
    <button class="btn btn-secondary" on:click={addProperty}>
      Add Property
    </button>
  </div>
</div>

<style lang="scss">
  .props-editor-section {
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 15px;
    margin-top: 15px;
    background: rgba(255, 255, 255, 0.7);
    
    h5 {
      margin: 0 0 15px 0;
      font-size: 0.85rem;
      font-weight: bold;
      color: #374151;
    }
  }
  
  .props-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .prop-row {
    display: grid;
    grid-template-columns: auto 1fr 1fr auto;
    gap: 12px;
    align-items: end;
    padding: 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    
    &:hover {
      background: #f3f4f6;
    }
  }
  
  .prop-number {
    display: flex;
    align-items: center;
    
    .number-badge {
      background: #8b5cf6;
      color: white;
      padding: 4px 8px;
      border-radius: 50%;
      font-size: 0.7rem;
      font-weight: bold;
      min-width: 24px;
      text-align: center;
    }
  }
  
  .prop-key-input,
  .prop-value-input {
    display: flex;
    flex-direction: column;
    
    label {
      font-size: 0.7rem;
      margin-bottom: 4px;
      color: #374151;
      font-weight: 500;
    }
    
    input, select {
      width: 100%;
      padding: 6px 8px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      background: #fff;
      font-size: 0.75rem;
      
      &:focus {
        outline: none;
        border-color: #8b5cf6;
        box-shadow: 0 0 0 1px #8b5cf6;
      }
    }
  }
  
  .key-field {
    font-family: monospace;
  }
  
  .value-field {
    font-family: monospace;
    font-size: 0.7rem;
  }
  
  .custom-value-field {
    margin-top: 4px;
    font-family: monospace;
  }
  
  .prop-actions {
    display: flex;
    align-items: center;
  }
  
  .props-footer {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e5e7eb;
    text-align: center;
  }
  
  .btn {
    padding: 6px 12px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-secondary {
      background: #8b5cf6;
      color: white;
      
      &:hover {
        background: #7c3aed;
      }
    }
    
    &.btn-danger {
      background: #ef4444;
      color: white;
      
      &:hover {
        background: #dc2626;
      }
    }
    
    &.btn-small {
      padding: 4px 6px;
      font-size: 10px;
    }
  }
</style>